<script setup>
/* Imports */
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

/* Store */
import { useGlobalStore } from '../store/global.js';

/* Init store */
const { getProductData } = storeToRefs(useGlobalStore());

/* Handle products */
const products = computed(() => getProductData.value?.products);

/* Props */
const props = defineProps({
  maxProducts: {
    type: Number,
    default: 2,
  },
  specName: {
    type: String,
    default: '',
  },
  specKey: {
    type: String,
    default: '',
  },
  specList: {
    type: Array,
    default: () => [],
  },
});

/* Get Specs */
const getSpecs = (specs) => {
  // Only display up to the max products
  const finalSpecs = specs.slice(0, props.maxProducts);

  // Check if we are showing less than the max products
  // if so, fill up the remaining spots with empty objects
  if (finalSpecs.length < props.maxProducts) {
    const emptyColumns = Math.abs(finalSpecs.length - props.maxProducts);

    for (let i = 0; i < emptyColumns; i += 1) {
      finalSpecs.push({});
    }
  }

  return finalSpecs;
};

const getProductName = (specPid) => {
  const selectedProduct = products.value.find((product) => product.pid === specPid);
  const productName = selectedProduct?.displayName ?? '';
  return productName;
};

const showSpecValue = (value) => value?.toLowerCase() !== 'no';
</script>

<template>
  <table
    class="table"
    :class="`columns-${props.maxProducts}`"
    :data-test="`${props.specKey}`"
  >
    <thead>
      <tr>
        <th
          colspan="5"
          v-html="props.specName"
        />
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="specValue in getSpecs(props.specList)"
          :key="specValue?.pid"
          :data-product-name="getProductName(specValue?.pid)"
        >
          <span
            v-if="showSpecValue(specValue?.specValue)"
            v-html="specValue?.specDisplayValue"
          />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style lang="scss" scoped>
$table-border: 1px solid $color-gray-85;

.table {
  display: table;
  table-layout: auto;
  @media print {
    page-break-inside: auto;
  }

  tr {
    @media print {
      page-break-inside: avoid;
      page-break-after: auto;
    }
  }

  &.columns-2 {

    td {
      width: 50%;
    }
  }

  &.columns-3 {

    td {
      width: 33.333%;
    }
  }

  &.columns-4 {

    td {
      width: 25%;
    }
  }

  &.columns-5 {

    td {
      width: 20%;
    }
  }

  th {
    background-color: $color-gray-95;
    font-size: 0.75rem;
    @include font-primary-weight-bold();
    color: $color-black;
    text-transform: uppercase;
    border: unset;
    // stylelint-disable-next-line declaration-no-important
    border-top: 1px solid $color-gray-85 !important;
    padding: 0.35rem 1rem;
    text-align: center;
    letter-spacing: initial;
    @include font-primary();

    a {
      font-size: 0.75rem;
    }

    @media (min-width: $compare-screen-small) {
      text-align: left;
    }

    @media print {
      // stylelint-disable-next-line declaration-no-important
      border-top: none !important;
      // stylelint-disable-next-line declaration-no-important
      border-bottom: $table-border !important;
    }
  }

  td {
    font-size: $font-size-small;
    border-right: $table-border;
    padding: 1rem;
    text-align: left;

    &:last-child {
      border-right: none;
    }

    @media print {
      // stylelint-disable-next-line declaration-no-important
      border-bottom: $table-border !important;

      &:first-child {
        border-left: $table-border;
      }

      &:last-child {
        border-right: $table-border;
      }
    }
  }
}
</style>
